import { Form, FormControl, FormField, FormItem, FormMessage, Input, cn } from '@cuspar/ui';
import { zodResolver } from '@hookform/resolvers/zod';
import { ReactNode } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { domainZodRule, domainZodRuleWithTld } from './zod/domain.zod';
import { Search } from 'lucide-react';
import { useTranslation } from 'react-i18next';

export function DomainSearchForm({
  onSubmit,
  children,
  defaultDomain,
  className,
  keepTld = false,
  placeholder,
}: {
  onSubmit: (values: { domain: string }) => void;
  children?: ReactNode;
  defaultDomain?: string;
  className?: string;
  keepTld?: boolean;
  placeholder?: string;
}) {
  const { t } = useTranslation();
  const formSchema = z.object({
    domain: keepTld ? domainZodRuleWithTld : domainZodRule,
  });

  // 1. Define a form.
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      domain: defaultDomain || '',
    },
  });

  // 2. Define a submit handler.
  function onFormSubmit(values: z.infer<typeof formSchema>) {
    onSubmit(values);
    // update the form with the new domain
    form.setValue('domain', values.domain);
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onFormSubmit)} className={cn('flex gap-4 justify-center items-center w-full', className)}>
        <FormField
          control={form.control}
          name='domain'
          render={({ field }) => (
            <FormItem className='w-full'>
              <FormControl>
                <Input
                  autoCapitalize='off'
                  autoCorrect='off'
                  autoComplete='off'
                  spellCheck={false}
                  icon={<Search className='absolute left-2 top-2.5 text-muted-foreground size-4' />}
                  placeholder={placeholder || t('domain.searchPlaceholder')}
                  {...field}
                ></Input>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        {children}
      </form>
    </Form>
  );
}
